/**
 * Turning a permission grant into something a component can ask questions of.
 *
 * ### The two key spaces
 *
 * This model has two, and mixing them up is the fastest way to ship a gate that is wrong in
 * the dangerous direction:
 *
 * | space               | example                                        | who speaks it                        |
 * | ------------------- | ---------------------------------------------- | ------------------------------------ |
 * | **permission name** | `can_get_customers`, `billing_admin`           | the grant, `usePermission`, features |
 * | **endpoint path**   | `/admin/customer-management/customers/get`     | nav entries, route metadata          |
 *
 * `ENDPOINT_PERMISSIONS` is the join between them. A session never carries paths; it carries
 * names. See `endpoint-permissions.ts`.
 *
 * ### Where the grant comes from
 *
 * `GET /rf-backend/staff-permissions` — see `services/permission.service.ts`. It is **not** the
 * id token's `permissions` claim, despite the legacy helper `getPermissionList()` reading that
 * one; the claim feeds a separate slice used by route filtering that filters nothing.
 *
 * Both are the same shape, which is exactly why the distinction is easy to miss and worth
 * stating twice.
 *
 * ### The shape
 *
 * A JSON object keyed by permission name with a truthy value, which is what the legacy
 * `permissions[name]` and `canRole` both index into. `normalisePermissionGrant` also accepts
 * an array of names, because the claim encoding is the other plausible one and the two sources
 * are not guaranteed to agree forever.
 *
 * Anything it cannot make sense of yields an EMPTY set, which denies. Failing open here would
 * mean a malformed response silently granting a staff console.
 */

/** The empty grant. Shared so callers can compare identity and skip work. */
export const NO_PERMISSIONS: ReadonlySet<string> = new Set<string>();

/**
 * A decoded grant → the set of permission names the session holds.
 *
 * Accepted encodings:
 * - `{ can_get_customers: true, billing_admin: 1 }` → keys with truthy values
 * - `['can_get_customers', 'billing_admin']`        → the strings
 * - anything else, including `null`                 → empty set (denies)
 *
 * An object entry with a falsy value is treated as NOT granted. The legacy check was
 * `permissions[name]` — a truthiness test, not a key-presence test — so `{ x: false }` denied
 * there and denies here.
 */
export const normalisePermissionGrant = (grant: unknown): ReadonlySet<string> => {
  if (Array.isArray(grant)) {
    const names = grant.filter((entry): entry is string => typeof entry === 'string' && !!entry);
    return names.length > 0 ? new Set(names) : NO_PERMISSIONS;
  }

  if (grant !== null && typeof grant === 'object') {
    const granted = Object.entries(grant as Record<string, unknown>)
      .filter(([, value]) => Boolean(value))
      .map(([name]) => name);
    return granted.length > 0 ? new Set(granted) : NO_PERMISSIONS;
  }

  return NO_PERMISSIONS;
};

/**
 * A JSON-encoded grant → the set of permission names.
 *
 * The id token carries its `permissions` claim as a **string**, so this parses first. Kept
 * because the claim is still read in one place — see `services/permission.service.ts` for why
 * it is not the gating source.
 */
export const parsePermissionClaim = (claim: string | undefined | null): ReadonlySet<string> => {
  if (!claim) return NO_PERMISSIONS;

  try {
    return normalisePermissionGrant(JSON.parse(claim));
  } catch {
    return NO_PERMISSIONS;
  }
};
